/**
 * Blockhash cache with 30s refresh and stale guard.
 *
 * Relocated from frontend/src/lib/tx-engine/blockhash-cache.ts as part of the
 * auth boundary migration (spec 009 FILE 14 Group E). The frontend version
 * registered a `document.visibilitychange` listener directly — forbidden in
 * SDK core (.claude/rules/platform-agnostic.md). Instead the foreground-
 * resume signal is injected via the optional `TxOnForeground` slot from
 * `TxEngineConfig` (web wires `adapter.appState`; RN wires `AppState`).
 *
 * Platform-neutral — safe in src/auth/ core.
 */

import type { Connection } from '@solana/web3.js';

import {
  BlockhashResolver,
  type BlockhashResolverMode,
  DEFAULT_BLOCKHASH_RESOLVER_MODE,
  type RecentBlockhash,
} from '../../blockchain/blockhash/index.js';
import { getBlockmetaStore } from '../../blockchain/blockmeta/index.js';
import type { TxEngineLogger, TxOnForeground } from '../config.js';
import { isValidTransactionTraceId } from '../diagnostics/lifecycle.js';
import type { CachedBlockhash } from '../types.js';

const REFRESH_INTERVAL_MS = 30_000;
const STALE_THRESHOLD_MS = 60_000;

export class BlockhashCache {
  private cached: CachedBlockhash | null = null;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private connection: Connection | null = null;
  private refreshFailed = false;
  private foregroundUnsub: (() => void) | null = null;
  private readonly onForeground: TxOnForeground | undefined;
  private readonly resolver: BlockhashResolver;

  constructor(
    logger: TxEngineLogger,
    onForeground?: TxOnForeground,
    resolveMode?: () => BlockhashResolverMode,
  ) {
    this.onForeground = onForeground;
    this.resolver = new BlockhashResolver({
      resolveMode: resolveMode ?? (() => DEFAULT_BLOCKHASH_RESOLVER_MODE),
      store: getBlockmetaStore(),
      logger,
    });
  }

  /** Start the automatic refresh interval. */
  start(connection: Connection): void {
    this.connection = connection;
    this.scheduleBackgroundRefresh(connection);

    this.intervalId = setInterval(() => {
      /* v8 ignore start -- defensive: stop() clears the interval before nulling connection, so the false branch never fires */
      if (this.connection) {
        this.scheduleBackgroundRefresh(this.connection);
      }
      /* v8 ignore stop */
    }, REFRESH_INTERVAL_MS);

    // Refresh on app foreground-resume (EC-9) — injected, platform-neutral.
    if (this.onForeground) {
      this.foregroundUnsub = this.onForeground(() => {
        if (this.connection) {
          this.scheduleBackgroundRefresh(this.connection);
        }
      });
    }
  }

  /**
   * Fire-and-forget refresh for interval / foreground paths. `refresh()` sets
   * `refreshFailed` and re-throws for explicit await callers; the resolver owns
   * the RPC failure log. Here we swallow the rejection so background work
   * cannot surface as unhandled.
   */
  private scheduleBackgroundRefresh(connection: Connection): void {
    void this.refresh(connection).catch(() => {
      // State + logging handled inside refresh().
    });
  }

  /** Stop the automatic refresh interval. */
  stop(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    if (this.foregroundUnsub) {
      this.foregroundUnsub();
      this.foregroundUnsub = null;
    }
    this.connection = null;
    this.cached = null;
    this.refreshFailed = false;
  }

  /** Get the cached blockhash if fresh (within 60s and no refresh failure). */
  get(): CachedBlockhash | null {
    if (!this.cached) {
      return null;
    }

    if (this.refreshFailed) {
      return null;
    }

    const age = Date.now() - this.cached.fetchedAt;
    if (age > STALE_THRESHOLD_MS) {
      return null;
    }

    return this.cached;
  }

  /** Force-refresh the cached blockhash. */
  async refresh(connection: Connection, traceId?: string): Promise<CachedBlockhash> {
    const safeTraceId = isValidTransactionTraceId(traceId) ? traceId : undefined;
    try {
      const result = await this.resolver.resolveBlockhashWithRpcExpiry(
        connection,
        safeTraceId === undefined ? undefined : { traceId: safeTraceId },
      );
      this.cached = {
        blockhash: result.blockhash,
        lastValidBlockHeight: result.lastValidBlockHeight,
        fetchedAt: result.cachedAtMs,
      };
      this.refreshFailed = false;
      return this.cached;
    } catch (e: unknown) {
      this.refreshFailed = true;
      throw e;
    }
  }

  async resolveRecent(connection: Connection, traceId?: string): Promise<RecentBlockhash> {
    const safeTraceId = isValidTransactionTraceId(traceId) ? traceId : undefined;
    const cached = this.get();
    const result = await this.resolver.resolveRecentBlockhash(
      connection,
      safeTraceId === undefined
        ? undefined
        : {
            traceId: safeTraceId,
            ...(cached === null ? {} : { suppressRpcFallbackWarning: true }),
          },
    );
    if (result.lastValidBlockHeight !== undefined) {
      this.cached = {
        blockhash: result.blockhash,
        lastValidBlockHeight: result.lastValidBlockHeight,
        fetchedAt: result.cachedAtMs,
      };
      this.refreshFailed = false;
    }
    return result;
  }
}
